import { defineStore } from 'pinia';
import type { Buerge } from '@/shared/Buergen/types';
import { useNutzerStore } from '@/shared/Nutzer/useNutzerStore';
import { useSharedApi } from '@/shared/api/useSharedApi.ts';
import { Vertragsart } from '@/types/Partner.ts';

interface StoreState {
  buergen: Map<string, Buerge>;
}

const api = useSharedApi().sharedAvalPartnerKonfigurator;

export const useBuergenStore = defineStore('BürgenStore', {
  state: (): StoreState => ({
    buergen: new Map<string, Buerge>(),
  }),

  actions: {
    async addBuergen(buergen: Buerge[]) {
      const newBuergen = buergen.filter((buerge) => !this.buergen.has(buerge.unternehmenId));
      if (newBuergen.length === 0) {
        return;
      }

      const ids = newBuergen
        .filter((b) => b.vertragsart === Vertragsart.REGULAR)
        .map((b) => b.unternehmenId);
      const bulkResult = await api.holeBuergenKommunikationBulk(ids);

      for (const buerge of newBuergen) {
        const result = bulkResult[buerge.unternehmenId];
        if (result) {
          buerge.prueftVertraege = result.prueftVertraege;
          buerge.allowedAlternativeText = result.allowedAlternativeText;
          buerge.allowedIndividualText = result.allowedIndividualText;
        }
        this.buergen.set(buerge.unternehmenId, buerge);
      }
    },
    ersetzeBuerge(buerge: Buerge) {
      this.buergen.set(buerge.unternehmenId, buerge);
    },
  },

  getters: {
    getBuergen(state): Buerge[] {
      return Array.from(state.buergen.values());
    },

    getBuergenWithVertragReview(state): Buerge[] {
      return Array.from(state.buergen.values()).filter((buerge) => buerge.prueftVertraege);
    },

    getBuergenWithVerwendungTexte(state): Buerge[] {
      return Array.from(state.buergen.values()).filter(
        (buerge) => buerge.verwendungBürgschaftstexte,
      );
    },

    getBuergeById(state): (id: string) => Buerge | undefined {
      return (id: string): Buerge | undefined => state.buergen.get(id);
    },

    getBuergeName(state): (id: string) => string {
      return (id: string): string => state.buergen.get(id).name;
    },
    isLieferkanalPdf(state): boolean {
      const nutzerStore = useNutzerStore();
      return !!state.buergen.get(nutzerStore.nutzer.organisation)?.lieferkanalPdf;
    },
  },
});
